// The shared agent voice pipeline: tap → record → /Agent/transcribe → sendQuestion(voice) →
// /Agent/speech → play, with an instant pre-generated acknowledgment clip masking agent latency.
// Used by both the inline mic button (single turn) and the VoiceOverlay (loop = re-arm after
// each answer). One instance per AgentPage — loop is read at flow time via a ref.
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";
import { useVoiceRecorder } from "./useVoiceRecorder";
import { useVoiceActivity, primeVoiceActivity } from "./useVoiceActivity";

const ACK_CLIPS = [
  "/audio/ack/ack-1.mp3",
  "/audio/ack/ack-2.mp3",
  "/audio/ack/ack-3.mp3",
  "/audio/ack/ack-4.mp3",
];

// Audible acknowledgment disabled: the clips play much louder than the TTS answer and get
// clipped when the answer arrives. The clip still "plays" MUTED on the stop tap — that
// gesture-initiated play() is what unlocks the shared audio element for the async answer
// playback (Safari/iOS). Flip to true to restore the audible ack.
const ACK_AUDIBLE = false;

// Backend AgentRequest.Question caps at 2000 chars; SpeechRequest.Text at 1000.
const MAX_QUESTION_LENGTH = 2000;
const MAX_SPOKEN_FALLBACK_LENGTH = 1000;

const REGION_UNAVAILABLE_MESSAGE = "Voice isn't available in your region yet.";

// One audio element shared by ack clips and TTS playback: playing the ack clip synchronously
// inside the stop-tap gesture unlocks this element, so the async TTS play() after the 5–20s
// agent round trip is allowed on Safari/iOS.
let sharedAudio = null;
const getAudio = () => {
  if (!sharedAudio) sharedAudio = new Audio();
  return sharedAudio;
};

// Rough markdown → speakable text for answers missing a spokenAnswer block.
const stripMarkdown = (text) =>
  (text || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/\|/g, " ")
    .replace(/^[#>\-*+\s]+/gm, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

// With responseType 'blob', axios delivers JSON error bodies as Blobs — unwrap them.
const parseApiError = async (err) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  }
  return data || null;
};

const speakWithBrowserTts = (text) =>
  new Promise((resolve) => {
    if (!("speechSynthesis" in window)) {
      resolve(false);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => resolve(true);
    utterance.onerror = () => resolve(true);
    window.speechSynthesis.speak(utterance);
  });

// autoDetect (Features:VoiceAutoDetect): true = hands-free (listen on open, VAD
// auto-submit, tap pauses); false = push-to-talk (tap to start, tap to send, mic
// off after each answer).
export const useVoiceConversation = ({ sendQuestion, loop = false, autoDetect = false }) => {
  // idle → listening → transcribing → thinking → speaking → (idle | listening), plus error.
  const [phase, setPhase] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  // Destructure so callbacks depend on the recorder's STABLE functions, never the
  // per-render object — an object dep made the unmount cleanup fire on every render,
  // silently cancelling the recording (the "tap does nothing" bug).
  const {
    start: startRecording,
    stop: stopRecording,
    cancel: cancelRecording,
    release: releaseMic,
    getStream,
    isSupported,
  } = useVoiceRecorder();
  // Live mic energy + auto-send countdown for the overlay visuals (quantized to
  // avoid re-rendering at the VAD sample rate).
  const [voiceActivity, setVoiceActivity] = useState({ level: 0, sendProgress: 0 });
  const lastActivityRef = useRef({ level: 0, sendProgress: 0 });

  const loopRef = useRef(loop);
  const autoDetectRef = useRef(autoDetect);
  const activeRef = useRef(false);
  const sendQuestionRef = useRef(sendQuestion);
  useEffect(() => {
    loopRef.current = loop;
  }, [loop]);
  useEffect(() => {
    autoDetectRef.current = autoDetect;
  }, [autoDetect]);
  useEffect(() => {
    sendQuestionRef.current = sendQuestion;
  }, [sendQuestion]);

  const primeAudioOutput = useCallback(() => {
    const audio = getAudio();
    // Never let a previous playback's handlers fire off this clip's events — a stale
    // onended resurrects an interrupted turn's frozen pipeline mid-conversation.
    audio.onended = null;
    audio.onerror = null;
    const clip = ACK_CLIPS[Math.floor(Math.random() * ACK_CLIPS.length)];
    audio.muted = !ACK_AUDIBLE;
    audio.src = clip;
    // Even if the clip 404s, the play() call inside the tap gesture is what unlocks the element.
    audio.play().catch(() => {});
  }, []);

  const playTtsBlob = useCallback(
    (blob) =>
      new Promise((resolve) => {
        const audio = getAudio();
        const url = URL.createObjectURL(new Blob([blob], { type: "audio/mpeg" }));
        const finish = () => {
          audio.onended = null;
          audio.onerror = null;
          URL.revokeObjectURL(url);
          resolve(true);
        };
        audio.onended = finish;
        audio.onerror = finish;
        audio.muted = false; // the muted unlock primer must not silence the answer
        audio.src = url;
        audio.play().catch(finish);
      }),
    []
  );

  const speak = useCallback(
    async (text) => {
      try {
        const blob = await api.post(
          "/Agent/speech",
          { text },
          { responseType: "blob" }
        );
        return await playTtsBlob(blob.data);
      } catch (err) {
        const body = await parseApiError(err);
        if (err?.response?.status === 503) {
          setUnavailable(true);
          setErrorMessage(body?.message || REGION_UNAVAILABLE_MESSAGE);
        }
        // Degraded but still spoken: browser voice beats silence mid-drive.
        return speakWithBrowserTts(text);
      }
    },
    [playTtsBlob]
  );

  const beginListening = useCallback(async () => {
    const started = await startRecording();
    if (started) {
      activeRef.current = true;
      setErrorMessage(null);
      setPhase("listening");
    } else {
      setPhase("error");
      setErrorMessage("Microphone access was denied or unavailable.");
    }
    return started;
  }, [startRecording]);

  const finishTurn = useCallback(async () => {
    if (autoDetectRef.current && loopRef.current && activeRef.current) {
      await beginListening();
    } else if (loopRef.current) {
      // Push-to-talk in an open overlay: fully manual — mic off until the next push,
      // but the stream stays retained so re-push is instant (no repeat OS popups).
      setPhase("idle");
    } else {
      activeRef.current = false;
      releaseMic(); // conversation over — drop any retained stream
      setPhase("idle");
    }
  }, [beginListening, releaseMic]);

  // VAD auto-submit produced nothing usable: in auto-detect mode just resume
  // listening quietly; in push-to-talk the user acted deliberately, so tell them.
  const handleEmptyTurn = useCallback(async () => {
    if (autoDetectRef.current && loopRef.current && activeRef.current) {
      await beginListening();
    } else {
      setPhase("error");
      setErrorMessage("Didn't catch that — tap to try again.");
    }
  }, [beginListening]);

  const stopAndSend = useCallback(async () => {
    primeAudioOutput(); // synchronous in the tap gesture — Safari audio unlock
    setPhase("transcribing");

    // In conversation (loop) mode the mic stream stays open between turns so the OS
    // mic-activation event fires once per conversation, not once per question.
    const recording = await stopRecording(loopRef.current);
    if (!recording || !recording.blob?.size) {
      await handleEmptyTurn();
      return;
    }

    try {
      const form = new FormData();
      form.append("audio", recording.blob, recording.fileName);
      const transcribeResponse = await api.post("/Agent/transcribe", form);
      const transcript = (transcribeResponse.data?.text || "").trim();

      if (!transcript) {
        await handleEmptyTurn();
        return;
      }

      setPhase("thinking");
      const response = await sendQuestionRef.current(
        transcript.slice(0, MAX_QUESTION_LENGTH),
        { voice: true }
      );

      const spoken =
        response?.spokenAnswer ||
        stripMarkdown(response?.answer).slice(0, MAX_SPOKEN_FALLBACK_LENGTH);

      if (spoken) {
        setPhase("speaking");
        await speak(spoken);
      }
      await finishTurn();
    } catch (err) {
      const body = await parseApiError(err);
      if (err?.response?.status === 503) {
        setUnavailable(true);
        setErrorMessage(body?.message || REGION_UNAVAILABLE_MESSAGE);
      } else {
        setErrorMessage(body?.message || body?.error || "Something went wrong — tap to try again.");
      }
      setPhase("error");
    }
  }, [primeAudioOutput, stopRecording, speak, finishTurn, handleEmptyTurn]);

  // Hands-free auto-submit: while listening, ~1.3s of silence after speech triggers
  // the same send the tap does. Only in conversation (loop) mode; no barge-in — the
  // detector never runs while the answer plays.
  const handleVoiceActivity = useCallback(({ level, sendProgress }) => {
    const quantized = {
      level: Math.round(level * 10) / 10,
      sendProgress: Math.round(sendProgress * 20) / 20,
    };
    const prev = lastActivityRef.current;
    if (quantized.level !== prev.level || quantized.sendProgress !== prev.sendProgress) {
      lastActivityRef.current = quantized;
      setVoiceActivity(quantized);
    }
  }, []);

  useVoiceActivity({
    active: autoDetect && loop && phase === "listening",
    getStream,
    onSpeechEnd: stopAndSend,
    onActivity: handleVoiceActivity,
  });

  // Entry point for the overlay-open click: unlocks audio output (and, in auto-detect
  // mode, the VAD AudioContext) inside the gesture. Auto-detect starts listening
  // immediately; push-to-talk opens idle and waits for the first push.
  const startConversation = useCallback(async () => {
    primeAudioOutput();
    if (autoDetectRef.current) {
      primeVoiceActivity();
      await beginListening();
    }
  }, [primeAudioOutput, beginListening]);

  const interrupt = useCallback(async () => {
    const audio = getAudio();
    // Detach before pausing: the interrupted turn's await never resumes, and its stale
    // handlers must not fire on later playbacks (that caused phantom re-arm collisions).
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    await finishTurn();
  }, [finishTurn]);

  // Tap while listening = pause auto-listen (discards the partial utterance, keeps the
  // mic stream open for instant resume). VAD owns sending, so tap-to-send is gone.
  const pauseListening = useCallback(() => {
    cancelRecording(true);
    setPhase("paused");
  }, [cancelRecording]);

  // Single entry point for every mic/overlay button tap; behavior depends on the
  // phase and mode: listening-tap pauses in auto-detect, sends in push-to-talk.
  const micTap = useCallback(async () => {
    if (unavailable) return;
    if (phase === "idle" || phase === "error" || phase === "paused") {
      await beginListening();
    } else if (phase === "listening") {
      if (autoDetect) {
        pauseListening();
      } else {
        await stopAndSend();
      }
    } else if (phase === "speaking") {
      await interrupt();
    }
    // transcribing/thinking taps are ignored — the turn is already in flight
  }, [phase, unavailable, autoDetect, beginListening, pauseListening, stopAndSend, interrupt]);

  // Hard stop: close overlay, leave page, or abandon the conversation.
  const stopAll = useCallback(() => {
    activeRef.current = false;
    cancelRecording();
    const audio = getAudio();
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setPhase("idle");
    setErrorMessage(null);
  }, [cancelRecording]);

  useEffect(() => () => stopAll(), [stopAll]);

  return {
    phase,
    errorMessage,
    unavailable,
    isSupported,
    micTap,
    stopAll,
    startConversation,
    voiceActivity,
  };
};

export default useVoiceConversation;
